// src/services/patientService.js - Complete Patient Service
import { apiService } from "./api";

export const patientService = {
  // Get all patients with facility filtering and search
  getPatients: async (search = "", skip = 0, limit = 100) => {
    try {
      const params = { skip, limit };
      if (search && search.trim()) {
        params.search = search.trim();
      }

      const response = await apiService.get("/patients", { params });

      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error("Error fetching patients:", error);

      // Enhanced error handling with fallback
      if (error.response?.status === 404 || error.response?.status === 500) {
        console.warn("Patient endpoint unavailable, using fallback data");
        return patientService._getFallbackPatients(search);
      }

      throw new Error(`Failed to fetch patients: ${error.message}`);
    }
  },

  // Create new patient
  createPatient: async (patientData) => {
    try {

      // Enhanced validation
      const requiredFields = [
        "first_name",
        "last_name",
        "address",
        "city",
        "state",
        "zip_code",
      ];
      const missingFields = requiredFields.filter(
        (field) => !patientData[field]?.trim()
      );

      if (missingFields.length > 0) {
        throw new Error(`Missing required fields: ${missingFields.join(", ")}`);
      }

      // Validate ZIP code format
      if (
        patientData.zip_code &&
        !/^\d{5}(-\d{4})?$/.test(patientData.zip_code.trim())
      ) {
        throw new Error("ZIP code must be in format 12345 or 12345-6789");
      }

      // Validate email if provided
      if (
        patientData.email &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(patientData.email)
      ) {
        throw new Error("Invalid email format");
      }

      // Clean and format the data
      const cleanedData = {
        ...patientData,
        first_name: patientData.first_name.trim(),
        last_name: patientData.last_name.trim(),
        address: patientData.address.trim(),
        city: patientData.city.trim(),
        state: patientData.state.trim().toUpperCase(),
        zip_code: patientData.zip_code.trim(),
        phone_number: patientData.phone_number?.trim() || null,
        email: patientData.email?.trim().toLowerCase() || null,
        address_notes: patientData.address_notes?.trim() || null,
        medical_record_number:
          patientData.medical_record_number?.trim() || null,
        weight: patientData.weight?.trim() || null,
        alternate_contacts: patientData.alternate_contacts || null,
        emergency_contact_name:
          patientData.emergency_contact_name?.trim() || null,
        emergency_contact_phone:
          patientData.emergency_contact_phone?.trim() || null,
      };

      // Handle insurance information
      if (cleanedData.insurance_information) {
        try {
          if (cleanedData.insurance_information.trim().startsWith("{")) {
            cleanedData.insurance_info = JSON.parse(
              cleanedData.insurance_information
            );
          } else {
            cleanedData.insurance_info = cleanedData.insurance_information;
          }
        } catch (e) {
          cleanedData.insurance_info = cleanedData.insurance_information;
        }
        delete cleanedData.insurance_information;
      }

      const response = await apiService.post("/patients", cleanedData);
      return response.data;
    } catch (error) {
      console.error("Error creating patient:", error);

      // Enhanced error messages
      if (error.response?.status === 409) {
        throw new Error("A patient with this information already exists");
      } else if (error.response?.status === 422) {
        const details = error.response.data?.detail;
        if (Array.isArray(details)) {
          const errorMessages = details.map((err) => err.msg).join(", ");
          throw new Error(`Validation error: ${errorMessages}`);
        } else {
          throw new Error(`Validation error: ${details || "Invalid data"}`);
        }
      } else if (error.response?.status === 400) {
        throw new Error(error.response.data?.detail || "Invalid patient data");
      } else if (
        error.message.startsWith("Missing required fields") ||
        error.message.includes("ZIP code") ||
        error.message.includes("Invalid email")
      ) {
        throw error; // Re-throw validation errors as-is
      }

      throw new Error(`Failed to create patient: ${error.message}`);
    }
  },

  // Get patient by ID
  getPatientById: async (patientId) => {
    try {
      const response = await apiService.get(`/patients/${patientId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching patient:", error);

      if (error.response?.status === 404) {
        throw new Error("Patient not found");
      }

      throw new Error(`Failed to fetch patient: ${error.message}`);
    }
  },

  // Update patient
  updatePatient: async (patientId, patientData) => {
    try {

      // Clean and validate the data similar to create
      const cleanedData = { ...patientData };

      // Clean string fields (skip arrays/objects like alternate_contacts)
      Object.keys(cleanedData).forEach((key) => {
        if (typeof cleanedData[key] === "string" && cleanedData[key] !== null) {
          cleanedData[key] = cleanedData[key].trim();
          // Remove empty strings (but keep nulls)
          if (cleanedData[key] === "") {
            cleanedData[key] = null;
          }
        }
      });

      // Handle insurance information
      if (cleanedData.insurance_information !== undefined) {
        try {
          if (
            cleanedData.insurance_information &&
            cleanedData.insurance_information.trim().startsWith("{")
          ) {
            cleanedData.insurance_info = JSON.parse(
              cleanedData.insurance_information
            );
          } else {
            cleanedData.insurance_info = cleanedData.insurance_information;
          }
        } catch (e) {
          cleanedData.insurance_info = cleanedData.insurance_information;
        }
        delete cleanedData.insurance_information;
      }

      const response = await apiService.put(
        `/patients/${patientId}`,
        cleanedData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating patient:", error);

      if (error.response?.status === 404) {
        throw new Error("Patient not found");
      } else if (error.response?.status === 409) {
        throw new Error("Update would create a duplicate record");
      } else if (error.response?.status === 422) {
        const details = error.response.data?.detail;
        if (Array.isArray(details)) {
          const errorMessages = details.map((err) => err.msg).join(", ");
          throw new Error(`Validation error: ${errorMessages}`);
        } else {
          throw new Error(`Validation error: ${details || "Invalid data"}`);
        }
      }

      throw new Error(`Failed to update patient: ${error.message}`);
    }
  },

  // Delete patient
  deletePatient: async (patientId) => {
    try {
      const response = await apiService.delete(`/patients/${patientId}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting patient:", error);

      if (error.response?.status === 404) {
        throw new Error("Patient not found");
      } else if (error.response?.status === 409) {
        throw new Error("Cannot delete patient with associated orders");
      }

      throw new Error(`Failed to delete patient: ${error.message}`);
    }
  },

  // Search patients
  searchPatients: async (searchQuery, limit = 50) => {
    try {
      const params = { q: searchQuery, limit };
      const response = await apiService.get("/patients/search", { params });
      return response.data;
    } catch (error) {
      console.error("Error searching patients:", error);
      // Fallback to regular getPatients
      return await patientService.getPatients(searchQuery, 0, limit);
    }
  },

  // Get patient statistics
  getPatientStats: async (patientId) => {
    try {
      const response = await apiService.get(`/patients/${patientId}/stats`);
      return response.data;
    } catch (error) {
      console.error("Error fetching patient stats:", error);

      // Fallback calculation
      try {
        const orders = await patientService.getPatientOrders(patientId);
        return {
          patient_id: patientId,
          total_orders: orders.length,
          completed_orders: orders.filter((o) => o.status === "completed")
            .length,
          cancelled_orders: orders.filter((o) => o.status === "cancelled")
            .length,
          pending_orders: orders.filter(
            (o) => !["completed", "cancelled"].includes(o.status)
          ).length,
          last_order_date:
            orders.length > 0
              ? new Date(
                  Math.max(
                    ...orders.map((o) => new Date(o.created_at).getTime())
                  )
                ).toISOString()
              : null,
          first_order_date:
            orders.length > 0
              ? new Date(
                  Math.min(
                    ...orders.map((o) => new Date(o.created_at).getTime())
                  )
                ).toISOString()
              : null,
          completion_rate:
            orders.length > 0
              ? (orders.filter((o) => o.status === "completed").length /
                  orders.length) *
                100
              : 0,
        };
      } catch (fallbackError) {
        return {
          patient_id: patientId,
          total_orders: 0,
          completed_orders: 0,
          cancelled_orders: 0,
          pending_orders: 0,
          last_order_date: null,
          first_order_date: null,
          completion_rate: 0,
        };
      }
    }
  },

  // Get patient orders
  getPatientOrders: async (patientId, skip = 0, limit = 50) => {
    try {
      const params = { skip, limit };
      const response = await apiService.get(`/patients/${patientId}/orders`, {
        params,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching patient orders:", error);

      // Fallback to filtering all orders
      try {
        const { orderService } = await import("./orderService");
        const allOrders = await orderService.getAllOrders({ limit: 1000 });
        return allOrders.filter(
          (order) => order.patient_id === parseInt(patientId)
        );
      } catch (fallbackError) {
        return [];
      }
    }
  },

  // Validate patient data
  validatePatientData: (patientData) => {
    const errors = {};

    // Required fields validation
    if (!patientData.first_name?.trim()) {
      errors.first_name = "First name is required";
    } else if (patientData.first_name.trim().length < 2) {
      errors.first_name = "First name must be at least 2 characters";
    }

    if (!patientData.last_name?.trim()) {
      errors.last_name = "Last name is required";
    } else if (patientData.last_name.trim().length < 2) {
      errors.last_name = "Last name must be at least 2 characters";
    }

    if (!patientData.address?.trim()) {
      errors.address = "Address is required";
    }

    if (!patientData.city?.trim()) {
      errors.city = "City is required";
    }

    if (!patientData.state?.trim()) {
      errors.state = "State is required";
    }

    if (!patientData.zip_code?.trim()) {
      errors.zip_code = "ZIP code is required";
    } else if (!/^\d{5}(-\d{4})?$/.test(patientData.zip_code.trim())) {
      errors.zip_code = "ZIP code must be in format 12345 or 12345-6789";
    }

    // Optional field validation
    if (
      patientData.phone_number &&
      !/^\+?[\d\s\-\(\)]+$/.test(patientData.phone_number)
    ) {
      errors.phone_number = "Invalid phone number format";
    }

    if (
      patientData.email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(patientData.email)
    ) {
      errors.email = "Invalid email format";
    }

    if (patientData.date_of_birth) {
      const birthDate = new Date(patientData.date_of_birth);
      const today = new Date();
      if (birthDate > today) {
        errors.date_of_birth = "Date of birth cannot be in the future";
      }

      const age = today.getFullYear() - birthDate.getFullYear();
      if (age > 150) {
        errors.date_of_birth = "Please check the date of birth";
      }
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  },

  // Fallback data for development
  _getFallbackPatients: (search = "") => {
    const fallbackPatients = [
      {
        id: 1,
        patient_id: "P001",
        first_name: "John",
        last_name: "Doe",
        date_of_birth: "1980-01-15",
        gender: "Male",
        phone_number: "(555) 123-4567",
        email: "john.doe@email.com",
        address: "123 Main Street",
        address_notes: "Apt 2B",
        city: "Los Angeles",
        state: "CA",
        zip_code: "90210",
        medical_record_number: "MRN001",
        facility_id: 1,
        created_at: new Date().toISOString(),
      },
      {
        id: 2,
        patient_id: "P002",
        first_name: "Jane",
        last_name: "Smith",
        date_of_birth: "1975-08-22",
        gender: "Female",
        phone_number: "(555) 987-6543",
        email: "jane.smith@email.com",
        address: "456 Oak Avenue",
        address_notes: "House",
        city: "Beverly Hills",
        state: "CA",
        zip_code: "90212",
        medical_record_number: "MRN002",
        facility_id: 1,
        created_at: new Date().toISOString(),
      },
      {
        id: 3,
        patient_id: "P003",
        first_name: "Maria",
        last_name: "Garcia",
        date_of_birth: "1990-03-10",
        gender: "Female",
        phone_number: "(555) 555-0123",
        email: "maria.garcia@email.com",
        address: "789 Pine Street",
        address_notes: "Unit 5",
        city: "Santa Monica",
        state: "CA",
        zip_code: "90401",
        medical_record_number: "MRN003",
        facility_id: 1,
        created_at: new Date().toISOString(),
      },
    ];

    if (!search || !search.trim()) {
      return fallbackPatients;
    }

    const searchTerm = search.toLowerCase();
    return fallbackPatients.filter(
      (patient) =>
        patient.first_name.toLowerCase().includes(searchTerm) ||
        patient.last_name.toLowerCase().includes(searchTerm) ||
        patient.address.toLowerCase().includes(searchTerm) ||
        patient.medical_record_number?.toLowerCase().includes(searchTerm) ||
        patient.phone_number?.includes(searchTerm) ||
        patient.email?.toLowerCase().includes(searchTerm) ||
        patient.patient_id?.toLowerCase().includes(searchTerm)
    );
  },
};
